<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import Dialog from '@/views/components/Dialog.vue';
import Confirm from '@/views/components/Confirm.vue';
import { TextBoxComponent, NumericTextBoxComponent } from "@syncfusion/ej2-vue-inputs";
import { DropDownListComponent } from "@syncfusion/ej2-vue-dropdowns";
import { Toast } from 'bootstrap';

const router = useRouter();
const route = useRoute();
const store = useFastWay();
const loader = useLoading();

const showToast = (message, variant = 'success') => {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const bgClass = variant === 'success' ? 'bg-success' : variant === 'danger' ? 'bg-danger' : 'bg-primary';

  const toastHtml = `
    <div class="toast align-items-center ${bgClass} text-white border-0" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;

  container.insertAdjacentHTML('beforeend', toastHtml);
  const toastElement = container.lastElementChild;
  const bsToast = new Toast(toastElement, { delay: 3000 });
  bsToast.show();

  toastElement.addEventListener('hidden.bs.toast', () => {
    toastElement.remove();
  });
};
let id = ref(0);
let loaded = ref(false);
let editing = ref(false);
let dialog = ref(null);
let dialogDelete = ref(null);
let confirm = ref(null);
let data = ref(null);
let projectsList = ref([]);
let evaluationAreasList = ref([]);
let summaries = ref([]);
let data_local = ref({
  id: 0,
  projectId: 0,
  project_name: '',
  year: 0,
  evaluationAreaId: 0,
  evaluation_area_description: '',
  sequence: 0,
  description: '',
  summary: '',
  comments: ''
});
const title = "Área de evaluación de proyecto";
const items = [
  {
    text: "Tablero",
    href: "/",
  },
  {
    text: "Áreas de evaluación de proyecto",
    href: "/sbg/projects/project_evaluation_areas",
  },
  {
    text: "Área de evaluación de proyecto",
    active: true,
  }
];

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

const buildContext = () => ({
  userId: store.userInfo.userId,
  login: store.userInfo.login || store.userInfo.userName || '',
  token: store.userInfo.token,
  dbName: store.userInfo.organizationName || ''
});

const summaryText = computed(() => {
  const match = summaries.value.find(s => s.code == data_local.value.summary);
  return match ? match.value : '';
});

onMounted(async () => {
  id.value = parseInt(route.params.id);
  await Promise.all([
    getData(id.value),
    loadProjectsList(),
    loadEvaluationAreasList(),
    loadSummariesList()
  ]);
});

const loadProjectsList = async () => {
  await axios
  .post(`${api.value}/sbg/projects/list`, {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    order: 'name',
    statusId: 'ACT'
  })
  .then(response => {
    if (response.data.success) {
      projectsList.value = response.data.data || [];
    }
  })
  .catch(() => {});
};

const loadEvaluationAreasList = async () => {
  await axios
  .post(`${api.value}/sbg/projects/evaluation_areas/list`, {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    order: 'description',
    context: store.userInfo
  })
  .then(response => {
    if (response.data.success) {
      evaluationAreasList.value = response.data.data || [];
    }
  })
  .catch(() => {});
};

const loadSummariesList = async () => {
  await axios
  .post(`${api.value}/lists/get_list`, {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    list: 'RESUMENAREAEVALUACION'
  })
  .then(response => {
    if (response.data.success) {
      summaries.value = response.data.data || [];
    }
  })
  .catch(() => {});
};

const getData = async (id) => {
  if (id == '0') {
    loaded.value = true;
    editing.value = true;
  } else {
    const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando datos del área de evaluación de proyecto'});
    await axios
    .post(`${api.value}/sbg/projects/project_evaluation_areas/data`, {id, context: buildContext()})
    .then(response => {
      if (response.data.success) {
        if (response.data.data) {
          data.value = response.data.data;
          data_local.value = JSON.parse(JSON.stringify(response.data.data));
        }
      } else {
        dialog.value.show('error', 'Error interno al cargar área de evaluación de proyecto', response.data.message);
      }
      loaded.value = true;
      loading.hide();
    })
    .catch(error => {
      loaded.value = true;
      loading.hide();
      dialog.value.show('error', `Error al cargar área de evaluación de proyecto ${id}`, error.message);
    });
  }
};

const resetData = () => {
  if (id.value == '0') {
    router.push({name: 'projectsProjectEvaluationAreas'});
  } else {
    data_local.value = JSON.parse(JSON.stringify(data.value));
    editing.value = false;
  }
};

const validateForm = () => {
  let validated = false;
  if (!data_local.value.projectId || data_local.value.projectId == 0) {
    dialog.value.show('error', 'Error al guardar área de evaluación de proyecto', 'Debe especificar un proyecto');
  } else if (!data_local.value.evaluationAreaId || data_local.value.evaluationAreaId == 0) {
    dialog.value.show('error', 'Error al guardar área de evaluación de proyecto', 'Debe especificar un área de evaluación');
  } else if (!data_local.value.year || data_local.value.year == 0) {
    dialog.value.show('error', 'Error al guardar área de evaluación de proyecto', 'Debe especificar el año');
  } else {
    validated = true;
  }
  return validated;
};

const save = async () => {
  if (validateForm()) {
    const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Guardando datos del área de evaluación de proyecto'});
    const payload = {
      context: buildContext(),
      id: id.value,
      projectId: data_local.value.projectId,
      year: data_local.value.year,
      evaluationAreaId: data_local.value.evaluationAreaId,
      sequence: data_local.value.sequence,
      description: data_local.value.description,
      summary: data_local.value.summary,
      comments: data_local.value.comments,
      createdBy: store.userInfo.userId
    };
    await axios
    .post(`${api.value}/sbg/projects/project_evaluation_areas/save`, payload)
    .then(response => {
      loading.hide();
      if (response.data.success) {
        if (response.data.data) {
          data.value = response.data.data;
          data_local.value = JSON.parse(JSON.stringify(response.data.data));
        }
        editing.value = false;
        showToast('Datos han sido actualizados', 'success');
        if (id.value == 0) {
          router.push({
            name: 'projectsProjectEvaluationArea',
            params: {id: response.data.data.id}
          });
        }
      } else {
        showToast(response.data.message, 'danger');
      }
    })
    .catch(() => {
      loading.hide();
      showToast(`Error al actualizar área de evaluación de proyecto ${id.value}`, 'danger');
    });
  }
};

const remove = async () => {
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Eliminando área de evaluación de proyecto'});
  const parms = {
    id: id.value,
    context: buildContext(),
    createdBy: store.userInfo.userId
  };
  await axios
  .post(`${api.value}/sbg/projects/project_evaluation_areas/delete`, parms)
  .then(response => {
    loading.hide();
    if (response.data.success) {
      dialogDelete.value.show('success', 'Información', 'Área de evaluación de proyecto fue eliminada correctamente');
    } else {
      dialog.value.show('error', 'Error al eliminar', response.data.message);
    }
  })
    .catch(() => {
      loading.hide();
      showToast(`Error al eliminar área de evaluación de proyecto ${id.value}`, 'danger');
    });
};

const add = () => {
  router.push({
    name: 'projectsProjectEvaluationArea',
    params: {id: 0}
  });
  id.value = 0;
  data_local.value = {
    id: 0,
    projectId: 0,
    project_name: '',
    year: (new Date()).getFullYear(),
    evaluationAreaId: 0,
    evaluation_area_description: '',
    sequence: 0,
    description: '',
    summary: '',
    comments: ''
  };
  editing.value = true;
};

const onProjectChange = (e) => {
  if (e.itemData) {
    data_local.value.projectId = e.itemData.id;
    data_local.value.project_name = e.itemData.name;
    data_local.value.year = e.itemData.year || (new Date()).getFullYear();
  }
};

const onEvaluationAreaChange = (e) => {
  if (e.itemData) {
    data_local.value.evaluationAreaId = e.itemData.id;
    data_local.value.evaluation_area_description = e.itemData.description;
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
          <div class="card mb-2">
              <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center align-middle row ms-0 me-0 mb-2">
                      <div v-if="!editing" class="col d-flex justify-content-start">
                        <b-button variant="success" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Agregar área de evaluación de proyecto" @click="add"><i class="las la-plus label-icon"></i> Agregar</b-button>
                      </div>
                      <div class="col"/>
                      <div class="input-group col justify-content-end">
                        <b-button v-if="editing" variant="primary" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Guardar cambios efectuados" @click="save"><i class="las la-save label-icon"></i> Guardar</b-button>
                        <b-button v-if="editing" variant="warning" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Descartar cambios efectuados" @click="resetData"><i class="las la-undo label-icon"></i> Cancelar</b-button>
                        <b-button v-if="!editing" variant="primary" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Editar información" @click="editing = true"><i class="las la-edit label-icon"></i> Editar</b-button>
                        <b-button v-if="!editing" variant="danger" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Eliminar área de evaluación de proyecto" @click="confirm.show('Confirme', '¿Está seguro de eliminar esta área de evaluación de proyecto?')"><i class="las la-trash label-icon"></i> Eliminar</b-button>
                      </div>
                  </div>
                  <hr>
                  <div class="row mb-4">
                    <div class="col-11">
                      <div class="form-floating">
                        <input v-if="!editing" type="text" class="form-control form-control-lg bg-light" id="project_name" v-model="data_local.project_name" placeholder="Proyecto" disabled/>
                        <DropDownListComponent v-else v-model:value="data_local.projectId" :dataSource="projectsList" :fields="{text: 'name', value: 'id'}" :enabled="editing" floatLabelType="Auto" placeholder="Proyecto" :allowFiltering="true" :filterBarPlaceholder="'Buscar proyecto'" @change="onProjectChange"/>
                        <label v-if="!editing" for="project_name">Proyecto</label>
                      </div>
                    </div>
                    <div class="col-1">
                      <div class="form-floating">
                        <NumericTextBoxComponent v-model:value="data_local.year" floatLabelType="Auto" placeholder="Año" format="n0" :disabled="id != 0" :showSpinButton="false"/>
                      </div>
                    </div>
                  </div>
                  <div class="row mb-4">
                    <div class="col-7">
                      <div class="form-floating">
                        <input v-if="!editing" type="text" class="form-control form-control-lg bg-light" id="evaluation_area" v-model="data_local.evaluation_area_description" placeholder="Área de Evaluación" disabled/>
                        <DropDownListComponent v-else v-model:value="data_local.evaluationAreaId" :dataSource="evaluationAreasList" :fields="{text: 'description', value: 'id'}" :enabled="editing" floatLabelType="Auto" placeholder="Área de Evaluación" :allowFiltering="true" :filterBarPlaceholder="'Buscar área'" @change="onEvaluationAreaChange"/>
                        <label v-if="!editing" for="evaluation_area">Área de Evaluación</label>
                      </div>
                    </div>
                    <div class="col-3">
                      <div class="form-floating">
                        <input v-if="!editing" type="text" class="form-control form-control-lg bg-light" id="summary" :value="summaryText" placeholder="Resumen" disabled/>
                        <DropDownListComponent v-else v-model:value="data_local.summary" :dataSource="summaries" :fields="{text: 'value', value: 'code'}" :enabled="editing" :showClearButton="true" floatLabelType="Auto" placeholder="Resumen"/>
                        <label v-if="!editing" for="summary">Resumen</label>
                      </div>
                    </div>
                    <div class="col-2">
                      <div class="form-floating">
                        <NumericTextBoxComponent v-model:value="data_local.sequence" floatLabelType="Auto" placeholder="Secuencia" format="n0" :disabled="!editing" :showSpinButton="false"/>
                      </div>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col">
                      <TextBoxComponent v-model:value="data_local.description" floatLabelType="Auto" placeholder="Descripción" :enabled="editing"/>
                    </div>
                  </div>
                  <div class="row mb-2">
                    <div class="col">
                      <TextBoxComponent v-model:value="data_local.comments" floatLabelType="Auto" placeholder="Comentarios" :enabled="editing" :multiline="true"/>
                    </div>
                  </div>
              </div>
          </div>
        </div>
    </div>
    <Dialog ref="dialog"/>
    <Dialog ref="dialogDelete" @accept="router.push({name: 'projectsProjectEvaluationAreas'});"/>
    <Confirm ref="confirm" @accept="remove"/>
    <div id="toast-container" class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 9999;"></div>
  </Layout>
</template>

<style scoped>
@import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-buttons/styles/material.css";

.e-input-group .e-input[disabled], .e-input-group.e-control-wrapper .e-input[disabled], .e-input-group.e-disabled, .e-input-group.e-control-wrapper.e-disabled, .e-float-input input[disabled], .e-float-input.e-control-wrapper input[disabled], .e-float-input textarea[disabled], .e-float-input.e-control-wrapper textarea[disabled], .e-float-input.e-disabled, .e-float-input.e-control-wrapper.e-disabled {
    -webkit-text-fill-color: rgba(0, 0, 0, 0.75);
    color: rgba(0, 0, 0, 0.75);
}
</style>
